import { SelectorPair } from './types'
import { selectors } from './selectors'

/**
 * Defines key UI states that must be met before a step can be shown.
 * e.g., a sidebar must be open to show a step inside it.
 */
export const stepPrerequisites: { readonly [key: string]: SelectorPair } = {
  backtestSideNav: {
    button: selectors.openSettingsButton,
    selector: selectors.backtestSideNavigationTabSettings,
  },
  rerunDialog: {
    button: selectors.rerunBacktestButton,
    selector: selectors.backtestRunDialog,
  },
  backtestTypeSelectMenu: {
    button: selectors.backtestTypeSelectButton,
    selector: selectors.backtestTypeSelectMenu,
  },
  alertSideNav: {
    button: selectors.alertSettingsButton,
    selector: selectors.alertSidePanel, // Target the panel itself
  },
  patternOpen: {
    button: selectors.userPattern,
    selector: selectors.patternBuyTab,
  },
  patternConditionExpanded: {
    button: selectors.patternConditionHeader,
    selector: selectors.conditionSelector,
  },
  patternSideNav: {
    button: selectors.patternConditionButton,
    selector: selectors.patternSidePanel,
  },
}

/**
 * Maps each tour step's target selector to the list of prerequisites that must be met
 * for it to be visible.
 */
export const dependencies: { readonly [key: string]: readonly SelectorPair[] } = {
  [selectors.backtestSideNavigationTabSettings]: [stepPrerequisites.backtestSideNav],
  [selectors.backtestSideNavigationTabReports]: [stepPrerequisites.backtestSideNav],
  [selectors.backtestSideNavigationTabPattern]: [stepPrerequisites.backtestSideNav],
  [selectors.createAlertBacktestButton]: [stepPrerequisites.backtestSideNav],
  [selectors.favoriteBacktestIconButton]: [stepPrerequisites.backtestSideNav],
  [selectors.shareBacktestIconButton]: [stepPrerequisites.backtestSideNav],
  [selectors.rerunBacktestButton]: [stepPrerequisites.backtestSideNav],
  [selectors.viewBacktestButton]: [stepPrerequisites.backtestSideNav],
  [selectors.backtestRunDialog]: [stepPrerequisites.backtestSideNav, stepPrerequisites.rerunDialog],
  [selectors.backtestRunDialogCost]: [
    stepPrerequisites.backtestSideNav,
    stepPrerequisites.rerunDialog,
  ],
  [selectors.backtestRunDialogPlay]: [
    stepPrerequisites.backtestSideNav,
    stepPrerequisites.rerunDialog,
  ],
  [selectors.backtestTypeSelectMenu]: [stepPrerequisites.backtestTypeSelectMenu],
  [selectors.alertSidePanel]: [stepPrerequisites.alertSideNav],
  [selectors.alertSidePanelSettingsTab]: [stepPrerequisites.alertSideNav],
  [selectors.alertSidePanelHistoryTab]: [stepPrerequisites.alertSideNav],
  [selectors.alertSidePanelPatternTab]: [stepPrerequisites.alertSideNav],
  [selectors.alertSidePanelActions]: [stepPrerequisites.alertSideNav],
  [selectors.patternItem]: [stepPrerequisites.patternOpen],
  [selectors.patternName]: [stepPrerequisites.patternOpen],
  [selectors.patternBuyTab]: [stepPrerequisites.patternOpen],
  [selectors.patternSellTab]: [stepPrerequisites.patternOpen],
  [selectors.triggerChartSelector]: [stepPrerequisites.patternOpen],
  [selectors.userPatternGroup]: [stepPrerequisites.patternOpen],
  [selectors.patternCondition]: [stepPrerequisites.patternOpen],
  [selectors.addRuleButton]: [stepPrerequisites.patternOpen],
  [selectors.conditionSelector]: [
    stepPrerequisites.patternOpen,
    stepPrerequisites.patternConditionExpanded,
  ],
  [selectors.patternConditionDragHandle]: [
    stepPrerequisites.patternOpen,
    stepPrerequisites.patternConditionExpanded,
  ],
  [selectors.savePatternButton]: [
    stepPrerequisites.patternOpen,
    stepPrerequisites.patternConditionExpanded,
  ],
  [selectors.patternConditionButton]: [
    stepPrerequisites.patternOpen,
    stepPrerequisites.patternConditionExpanded,
  ],
  [selectors.patternAlertsTab]: [stepPrerequisites.patternSideNav],
  [selectors.patternBacktestsTab]: [stepPrerequisites.patternSideNav],
  [selectors.patternVersionTab]: [stepPrerequisites.patternSideNav],
  [selectors.patternActionsSection]: [stepPrerequisites.patternSideNav],
}
